import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell,
  Check,
  Trash2,
  Info,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  X,
  Link as LinkIcon,
  Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminNotifications } from '../hooks/useAdminNotifications';
import type { NotificationType } from '../types/notifications';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ isOpen, onClose }) => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useAdminNotifications();

  // Log para depuração
  useEffect(() => {
    console.log('NotificationsPanel - Estado atual:', {
      isOpen,
      notificationsCount: notifications.length,
      unreadCount,
      loading
    });
  }, [isOpen, notifications.length, unreadCount, loading]);

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    console.log('Marcando notificação como lida:', notificationId);
    await markAsRead(notificationId);
  }, [markAsRead]);

  const handleMarkAllAsRead = useCallback(async () => {
    console.log('Marcando todas as notificações como lidas');
    await markAllAsRead();
  }, [markAllAsRead]);

  const handleDeleteNotification = useCallback(async (notificationId: string) => {
    console.log('Deletando notificação:', notificationId);
    await deleteNotification(notificationId);
  }, [deleteNotification]);

  const getNotificationIcon = (type: NotificationType) => {
    const iconProps = { className: 'w-5 h-5' };
    switch (type) {
      case 'info':
        return <Info {...iconProps} className="text-blue-500" />;
      case 'success':
        return <CheckCircle2 {...iconProps} className="text-green-500" />;
      case 'warning':
        return <AlertTriangle {...iconProps} className="text-yellow-500" />;
      case 'error':
        return <AlertCircle {...iconProps} className="text-red-500" />;
      default:
        return <Bell {...iconProps} className="text-gray-500" />;
    }
  };

  const getNotificationStyle = (type: NotificationType) => {
    switch (type) {
      case 'info':
        return 'bg-blue-500/10 border-blue-500/20';
      case 'success':
        return 'bg-green-500/10 border-green-500/20';
      case 'warning':
        return 'bg-yellow-500/10 border-yellow-500/20';
      case 'error':
        return 'bg-red-500/10 border-red-500/20';
      default:
        return 'bg-gray-500/10 border-gray-500/20';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0" style={{ zIndex: 200 }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="absolute right-0 top-0">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="w-96 max-h-[80vh] bg-background rounded-lg shadow-xl border border-border overflow-hidden absolute right-[300px] top-44"
        >
          {/* Cabeçalho */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-background z-10">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Notificações</h2>
              {unreadCount > 0 && (
                <span className="px-2 py-1 bg-primary/20 text-primary text-sm rounded-full">
                  {unreadCount} nova{unreadCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleMarkAllAsRead}
                  className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                  title="Marcar todas como lidas"
                >
                  <Check className="w-4 h-4 text-primary" />
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="p-2 hover:bg-white/5 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </motion.button>
            </div>
          </div>

          {/* Lista de Notificações */}
          <div className="overflow-y-auto max-h-[calc(80vh-4rem)]">
            {loading ? (
              <div className="p-8 flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-gray-400">A carregar notificações...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 flex flex-col items-center justify-center gap-4">
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center"
                >
                  <Bell className="w-8 h-8 text-gray-400" />
                </motion.div>
                <p className="text-gray-400 text-center">
                  Não tens notificações no momento
                </p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className={`p-4 relative ${
                      !notification.read ? 'bg-white/5' : ''
                    } hover:bg-white/10 transition-colors group`}
                  >
                    {!notification.read && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                    )}
                    <div className="flex gap-4">
                      <div className={`
                        p-2 rounded-xl ${
                          !notification.read ? 'animate-pulse' : ''
                        } ${getNotificationStyle(notification.type)}`}
                      >
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-medium text-sm">
                              {notification.title}
                            </h3>
                            <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!notification.read && (
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleMarkAsRead(notification.id)}
                                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                                title="Marcar como lida"
                              >
                                <Check className="w-4 h-4 text-primary" />
                              </motion.button>
                            )}
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeleteNotification(notification.id)}
                              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                              title="Apagar notificação"
                            >
                              <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
                            </motion.button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs text-gray-500">
                            {new Date(notification.created_at).toLocaleDateString('pt-PT', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {notification.link && (
                            <Link
                              to={notification.link}
                              onClick={() => {
                                handleMarkAsRead(notification.id);
                                onClose();
                              }}
                              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                            >
                              <LinkIcon className="w-3 h-3" />
                              Ver mais
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotificationsPanel; 